import { useEffect, useState } from 'react';

import { gdprApi, type Consent } from '@/api/queries/gdpr';
import { Button, Modal, useToast } from '@/components/ui';

import { ConsentStatusPill } from './ConsentStatusPill';

const TEMPLATES = [
  { value: 'renewal_default', label: 'Default renewal' },
  { value: 'renewal_newsletter', label: 'Newsletter' },
  { value: 'renewal_research', label: 'Research' },
  { value: 'renewal_dsar', label: 'DSAR' },
] as const;

const LANGS = ['auto', 'cs', 'sk', 'en', 'de'] as const;

type Props =
  | { mode: 'one'; target: Consent; onClose: () => void }
  | { mode: 'bulk'; consentIds: string[]; onClose: () => void };

export function ResendConsentModal(props: Props) {
  const resendOne = gdprApi.useResendOne();
  const resendBulk = gdprApi.useBulkResend();
  const preview = gdprApi.usePreviewRenewal();
  const toast = useToast();
  const [template, setTemplate] = useState('renewal_default');
  const [lang, setLang] = useState('auto');
  const [oneClickUnsub, setOneClickUnsub] = useState(props.mode === 'bulk');
  const [scheduleAt, setScheduleAt] = useState<string>('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');

  useEffect(() => {
    if (props.mode !== 'one') return;
    preview
      .mutateAsync({ id: props.target.id, template, lang })
      .then((r) => {
        setPreviewSubject(r.subject);
        setPreviewBody(r.html);
      })
      .catch(() => {
        setPreviewSubject('');
        setPreviewBody('');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, lang]);

  const submit = async () => {
    try {
      if (props.mode === 'one') {
        await resendOne.mutateAsync({
          id: props.target.id,
          body: {
            template,
            lang,
            oneClickUnsub,
            scheduleAt: scheduleAt || undefined,
          },
        });
        toast.show({ severity: 'success', title: 'Renewal email queued' });
      } else {
        const res = await resendBulk.mutateAsync({
          consentIds: props.consentIds,
          template,
          lang,
          oneClickUnsub,
          scheduleAt: scheduleAt || undefined,
        });
        toast.show({
          severity: 'success',
          title: `Queued ${res.queued} renewal emails`,
        });
      }
      props.onClose();
    } catch (err) {
      toast.show({
        severity: 'error',
        title: err instanceof Error ? err.message : 'Failed to enqueue',
      });
    }
  };

  return (
    <Modal
      open
      onClose={props.onClose}
      size="lg"
      title={props.mode === 'one' ? 'Resend renewal email' : `Bulk resend (${props.consentIds.length})`}
      subtitle={
        props.mode === 'one'
          ? 'Renders server-side so the preview matches what will be sent.'
          : 'Throttled at 200/hour. RFC 8058 1-click unsubscribe is on by default for bulk.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={resendOne.isPending || resendBulk.isPending}
            onClick={submit}
          >
            {scheduleAt ? 'Schedule send' : 'Send now'}
          </Button>
        </>
      }
    >
      {props.mode === 'one' && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            padding: 10,
            marginBottom: 12,
            background: 'var(--surface-2)',
            borderRadius: 6,
          }}
        >
          <div style={{ flex: 1 }}>
            <strong>{props.target.contactName || props.target.contactEmail || '—'}</strong>
            {props.target.contactEmail && props.target.contactName && (
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{props.target.contactEmail}</div>
            )}
          </div>
          <ConsentStatusPill status={props.target.status} expiresAt={props.target.expiresAt} />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>Template</span>
          <select
            className="ds-input"
            value={template}
            onChange={(e) => setTemplate(e.currentTarget.value)}
          >
            {TEMPLATES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>Language</span>
          <select
            className="ds-input"
            value={lang}
            onChange={(e) => setLang(e.currentTarget.value)}
          >
            {LANGS.map((l) => (
              <option key={l} value={l}>
                {l === 'auto' ? 'auto (use consent language)' : l}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label style={{ display: 'grid', gap: 4, marginTop: 10 }}>
        <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>
          Schedule send (optional)
        </span>
        <input
          type="datetime-local"
          className="ds-input"
          value={scheduleAt}
          onChange={(e) => setScheduleAt(e.currentTarget.value)}
        />
      </label>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
        <input
          type="checkbox"
          checked={oneClickUnsub}
          onChange={(e) => setOneClickUnsub(e.currentTarget.checked)}
        />
        <span style={{ fontSize: 13 }}>
          Include RFC 8058 List-Unsubscribe header (1-click unsubscribe)
        </span>
      </label>

      {props.mode === 'one' && (
        <div style={{ marginTop: 14 }}>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-2)',
              fontWeight: 500,
              marginBottom: 6,
            }}
          >
            Preview
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 6 }}>
            <div
              style={{
                padding: '8px 12px',
                background: 'var(--surface-2)',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {previewSubject || '—'}
            </div>
            <div
              style={{
                padding: 12,
                maxHeight: 280,
                overflow: 'auto',
                fontSize: 13,
              }}
               
              dangerouslySetInnerHTML={{
                __html:
                  previewBody ||
                  '<p style="color: var(--text-3); margin: 0;">Loading preview…</p>',
              }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
